import React, { useRef } from 'react';
import { createComponentPreviewEntry } from '@wix/editor-elements-integrations';
import type { PreviewWrapperProps } from '@wix/editor-elements-types/thunderboltPreview';
import {
  usePreviewEffect,
  usePreviewState,
  usePrevious,
  useResetComponent,
} from '@wix/editor-elements-preview-utils';
import {
  ITextAreaInputImperativeActions,
  ITextAreaInputProps,
} from '../TextAreaInput.types';
import style from './style/TextAreaInput.scss';

const noop = () => {};

export default (ViewerComponent: React.ComponentType<ITextAreaInputProps>) =>
  createComponentPreviewEntry(
    React.forwardRef<ITextAreaInputImperativeActions, ITextAreaInputProps>(
      (
        {
          previewWrapperProps = {},
          ...viewerProps
        }: PreviewWrapperProps<
          ITextAreaInputProps,
          {
            shouldResetComponent?: boolean;
            compPreviewState?: string;
            componentViewMode?: string;
          }
        >,
        ref,
      ) => {
        const { shouldResetComponent, compPreviewState, componentViewMode } =
          previewWrapperProps;
        const {
          id,
          value = '',
          setValidityIndication = noop,
          validateValue = noop,
          onInput = noop,
        } = viewerProps;

        const defaultValue = useRef(value);
        const previousViewMode = usePrevious(componentViewMode);

        React.useEffect(() => {
          if (previousViewMode === 'editor' && componentViewMode === 'editor') {
            defaultValue.current = value;
          }
        }, [value, previousViewMode, componentViewMode]);

        usePreviewEffect({
          componentViewMode,
          onPreviewViewMode: validateValue,
        });

        useResetComponent({
          shouldResetComponent,
          id,
          onResetComponent: () => {
            onInput({
              currentTarget: {
                value: defaultValue.current,
              } as any,
            } as any);
            setValidityIndication(false);
            validateValue();
          },
        });

        usePreviewState(id, compPreviewState, {
          focus: {
            selectors: `.${style.textarea}`,
            type: 'single',
          },
          error: {
            selectors: `.${style.textarea}`,
            type: 'single',
            state: 'invalid',
          },
          hover: {
            selectors: `.${style.textarea}`,
            type: 'single',
          },
          disabled: {
            selectors: `.${style.textarea}`,
            type: 'single',
          },
        });

        const shouldShowValidityIndication =
          compPreviewState === 'error' ||
          viewerProps.shouldShowValidityIndication;

        return (
          <ViewerComponent
            {...viewerProps}
            ref={ref}
            shouldShowValidityIndication={shouldShowValidityIndication}
            componentViewMode={componentViewMode}
          />
        );
      },
    ),
  );
